<template>
  <div>
    <div class="mb-2 text-primary font-medium-2">
      <span v-if="type === 'new'">{{
        t('Consignment ID from which special offer was created:')
      }}</span>
      <span v-else>{{ t('Consignment ID for which the special offer was held:') }}</span>

      <span class="font-weight-bold">{{ $route.params.consignmentId }}</span>
    </div>

    <b-overlay
      variant="white"
      :show="submitting"
      spinner-variant="primary"
      blur="0"
      opacity=".75"
      rounded="sm"
    >
      <validation-observer ref="AdminSpecialOfferDetailForm">
        <b-form ref="AdminSpecialOfferDetailFormElement" class="" @submit.prevent="submit">
          <b-row>
            <b-col sm="6" lg="4">
              <b-form-group>
                <div class="d-flex form-group-label">
                  <label for="status">{{ t('Provider') }}</label>
                  <span class="text-danger">*</span>
                </div>
                <validation-provider #default="{ errors }" :name="t('Provider')" rules="required">
                  <b-form-select
                    id="providerId"
                    v-model="values.providerId"
                    name="providerId"
                    tabindex="1"
                    :options="providerOptions"
                    class="mb-0"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>
            <b-col v-if="type !== 'new'" sm="6" lg="4">
              <b-form-group>
                <div class="d-flex form-group-label">
                  <label for="status">{{ t('Status') }}</label>
                  <span class="text-danger">*</span>
                </div>
                <validation-provider #default="{ errors }" :name="t('Status')" rules="required">
                  <b-form-select
                    id="status"
                    v-model="values.status"
                    name="status"
                    tabindex="2"
                    :options="offerStatusOptions"
                    class="mb-0"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>

            <b-col sm="6" lg="4">
              <b-form-group>
                <div class="d-flex form-group-label">
                  <label for="estimatedReturnCost">{{ t('Estimated Return Cost') }}</label>
                  <span class="text-danger">*</span>
                </div>
                <validation-provider
                  #default="{ errors }"
                  :name="t('Estimated Return Cost')"
                  rules="required|positive"
                >
                  <b-form-input
                    id="estimatedReturnCost"
                    v-model="values.estimatedReturnCost"
                    name="estimatedReturnCost"
                    :state="errors.length > 0 ? false : null"
                    :placeholder="t('Estimated Return Cost')"
                    tabindex="3"
                    type="number"
                    step="0.0001"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>

            <b-col sm="6" lg="4">
              <b-form-group>
                <div class="d-flex form-group-label">
                  <label for="additionalCost">{{ t('Additional Cost') }}</label>
                  <span class="text-danger">*</span>
                </div>
                <validation-provider
                  #default="{ errors }"
                  :name="t('Additional Cost')"
                  rules="required|positive"
                >
                  <b-form-input
                    id="additionalCost"
                    v-model="values.additionalCost"
                    name="additionalCost"
                    :state="errors.length > 0 ? false : null"
                    :placeholder="t('Additional Cost')"
                    tabindex="4"
                    type="number"
                    step="0.0001"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>

            <b-col sm="6" lg="4">
              <b-form-group>
                <div class="d-flex form-group-label">
                  <label for="ruleCost">{{ t('Rule Cost') }}</label>
                  <span class="text-danger">*</span>
                </div>
                <validation-provider
                  #default="{ errors }"
                  :name="t('Rule Cost')"
                  rules="required|positive"
                >
                  <b-form-input
                    id="ruleCost"
                    v-model="values.ruleCost"
                    name="ruleCost"
                    :state="errors.length > 0 ? false : null"
                    :placeholder="t('Rule Cost')"
                    tabindex="5"
                    type="number"
                    step="0.0001"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>

            <b-col sm="6" lg="4">
              <b-form-group>
                <div class="d-flex form-group-label">
                  <label for="serviceFee">{{ t('Service Fee') }}</label>
                  <span class="text-danger">*</span>
                </div>
                <validation-provider
                  #default="{ errors }"
                  :name="t('Service Fee')"
                  rules="required|positive"
                >
                  <b-form-input
                    id="serviceFee"
                    v-model="values.serviceFee"
                    name="serviceFee"
                    :state="errors.length > 0 ? false : null"
                    :placeholder="t('Service Fee')"
                    tabindex="6"
                    type="number"
                    step="0.0001"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>

            <b-col sm="6" lg="4">
              <b-form-group>
                <div class="d-flex form-group-label">
                  <label for="providerBaseCost">{{ t('Provider Base Cost') }}</label>
                  <span class="text-danger">*</span>
                </div>
                <validation-provider
                  #default="{ errors }"
                  :name="t('Provider Base Cost')"
                  rules="required|positive"
                >
                  <b-form-input
                    id="providerBaseCost"
                    v-model="values.providerBaseCost"
                    name="providerBaseCost"
                    :state="errors.length > 0 ? false : null"
                    :placeholder="t('Provider Base Cost')"
                    tabindex="7"
                    type="number"
                    step="0.0001"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>

            <b-col sm="6" lg="4">
              <b-form-group>
                <div class="d-flex form-group-label">
                  <label for="totalCost">{{ t('Total Cost') }}</label>
                  <span class="text-danger">*</span>
                </div>
                <validation-provider
                  #default="{ errors }"
                  :name="t('Total Cost')"
                  rules="required|positive"
                >
                  <b-form-input
                    id="totalCost"
                    v-model="values.totalCost"
                    name="totalCost"
                    :state="errors.length > 0 ? false : null"
                    :placeholder="t('Total Cost')"
                    tabindex="8"
                    type="number"
                    step="0.0001"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>

            <b-col cols="12">
              <b-button
                variant="primary"
                class="mt-1 d-flex align-items-center"
                type="submit"
                tabindex="9"
              >
                {{
                  submitting
                    ? t('Please Wait')
                    : type === 'new'
                    ? t('Create Special Offer')
                    : t('Edit Special Offer')
                }}
              </b-button>
            </b-col>
          </b-row>
        </b-form>
      </validation-observer>
    </b-overlay>
  </div>
</template>
<script>
import {
  BCol,
  BForm,
  BFormGroup,
  BFormInput,
  BRow,
  BFormSelect,
  BButton,
  BOverlay
} from 'bootstrap-vue';
import { ValidationObserver, ValidationProvider } from 'vee-validate';
import { useUtils as useI18nUtils } from '@core/libs/i18n';
import { mapGetters } from 'vuex';
import { helper } from '@core/mixins/helper.js';

export default {
  components: {
    BOverlay,
    BButton,
    BFormInput,
    BFormGroup,
    BRow,
    BCol,
    BForm,
    BFormSelect,

    ValidationObserver,
    ValidationProvider
  },
  mixins: [helper],
  props: {
    type: {
      type: String,
      default: 'new'
    },
    submitting: {
      type: Boolean,
      default: false
    },
    values: {
      type: Object,
      default() {
        return {
          providerId: '',
          status: '',
          estimatedReturnCost: '',
          totalCost: '',
          additionalCost: '',
          ruleCost: '',
          serviceFee: '',
          providerBaseCost: ''
        };
      }
    }
  },
  data() {
    return {
      offerStatusList: [],
      providers: []
    };
  },
  computed: {
    ...mapGetters({
      countries: 'app/countries',
      states: 'app/consignorStates',
      cities: 'app/consignorCities'
    }),

    offerStatusOptions() {
      const options = [{ text: this.$t('Make a Choice'), value: '' }];
      if (this.offerStatusList && this.offerStatusList.length > 0) {
        options.push(
          ...this.offerStatusList.map(item => ({
            text: item.info,
            value: item.value
          }))
        );
      }

      return options;
    },

    providerOptions() {
      const options = [{ text: this.$t('Make a Choice'), value: '' }];
      if (this.providers && this.providers.length > 0) {
        options.push(
          ...this.providers.map(item => ({
            text: item.name,
            value: item.id
          }))
        );
      }

      return options;
    }
  },
  async created() {
    this.offerStatusList = await this.getOfferStatus();
    this.providers = await this.getProviders({ page: 1, limit: 1000 });
  },
  methods: {
    submit() {
      this.$refs.AdminSpecialOfferDetailForm.validate().then(success => {
        if (success) {
          const data = {
            ...this.values
          };

          if (!data.status) {
            data.status = 1;
          }

          this.$emit('submit', data);
        }
      });
    },

    resetForm() {
      this.$refs.AdminSpecialOfferDetailFormElement.reset();
    }
  },
  setup() {
    const { t } = useI18nUtils();

    return {
      t
    };
  }
};
</script>
